import { useLocation, Link } from '@tanstack/react-router'
import { useSelector } from 'react-redux'
import { selectSidebarOverride } from '@/store/sidebar-override.slice'
import {
	OnboardingChecklist, type OnboardingChecklistItem,
	Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent,
	SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
	SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem,
	useSidebar, cn,
} from '@rentflowdev/ui'
import { ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	useGetCompanyProfileQuery,
	useListConnectionsQuery,
	useListDocumentTemplatesQuery,
} from '@/api'
import { opens, useAccess } from '@/lib/access'
import { useIsPlatformAdmin } from '@/lib/use-platform-admin'
import { NAV_ITEMS } from './nav-items'

function SidebarOnboarding() {
	const { t } = useTranslation()
	const { open } = useSidebar()

	const { data: companyProfile } = useGetCompanyProfileQuery()
	const { data: connectionsData } = useListConnectionsQuery()
	const { data: templatesData } = useListDocumentTemplatesQuery({ limit: 1 })

	const items = useMemo<OnboardingChecklistItem[]>(() => {
		const conns = connectionsData?.connections ?? []
		const hasListingPlatform = conns.some((c) => c.isActive && (c.platform === 'AVITO' || c.platform === 'CIAN'))
		const hasPaymentGateway  = conns.some((c) => c.isActive && c.platform === 'SITE')
		return [
			{ id: 'requisites',     label: t('dashboard.onboarding.items.requisites'),     done: !!companyProfile?.inn },
			{ id: 'templates',      label: t('dashboard.onboarding.items.templates'),      done: (templatesData?.total ?? 0) > 0 },
			{ id: 'platforms',      label: t('dashboard.onboarding.items.platforms'),      done: hasListingPlatform },
			{ id: 'paymentGateway', label: t('dashboard.onboarding.items.paymentGateway'), done: hasPaymentGateway },
		]
	}, [companyProfile, templatesData, connectionsData, t])

	if (!open) return null
	return (
		<div className="px-2 pb-2 pt-1">
			<OnboardingChecklist items={items} title={t('dashboard.onboarding.title')} completedLabel={t('dashboard.onboarding.completedLabel')} className="w-full" />
		</div>
	)
}

function SidebarLogo() {
	const { open } = useSidebar()
	return (
		<SidebarHeader className={cn(
			'h-15 flex-row items-center py-2',
			open ? 'justify-start px-3' : 'justify-center px-0',
		)}>
			<span className="text-3xl font-[600] text-(--color-muted-foreground) tracking-[-0.75px] leading-9 whitespace-nowrap">
				{open ? 'RentFlow' : 'R'}
			</span>
		</SidebarHeader>
	)
}

export function AppSidebar() {
	const { t } = useTranslation()
	const location = useLocation()
	const sidebarOverride = useSelector(selectSidebarOverride)
	const { state, setOpen } = useSidebar()
	const [openMenus, setOpenMenus] = useState<string[]>([])
	const isPlatformAdmin = useIsPlatformAdmin()
	const access = useAccess()
	// Only what the role reaches: a section without its grant would answer 403.
	const navItems = useMemo(
		() => NAV_ITEMS
			.filter((item) => !item.platformAdmin || isPlatformAdmin)
			.filter((item) => opens(access, item.requires))
			.map((item) => item.children
				? { ...item, children: item.children.filter((child) => opens(access, child.requires)) }
				: item)
			.filter((item) => !item.children || item.children.length > 0),
		[isPlatformAdmin, access],
	)

	const toggleMenu = (path: string) => {
		setOpenMenus(prev =>
			prev.includes(path) ? [] : [path]
		)
	}

	/**
	 * Groups only expand a submenu, and the submenu is hidden while the sidebar
	 * is collapsed to icons — so clicking a group icon did nothing visible and
	 * read as a dead button. Collapsed, the click now opens the sidebar and
	 * expands that group, which is the only way to reach its children.
	 */
	const handleGroupClick = (path: string) => {
		if (state === 'collapsed') {
			setOpen(true)
			setOpenMenus([path])
			return
		}
		toggleMenu(path)
	}

	return (
		<Sidebar collapsible="icon" className="border-r border-(--color-border)">
			<SidebarLogo />
			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							{navItems.map((item) => {
								const isOpen = openMenus.includes(item.path)

								if (item.children) {
									return (
										<SidebarMenuItem key={item.path}>
											<SidebarMenuButton
												render={<button type="button" onClick={() => handleGroupClick(item.path)} />}
												className="h-11 px-3 gap-2 text-sm rounded-(--radius-sm) group-data-[collapsible=icon]:gap-0"
												isActive={false}
												tooltip={t(item.labelKey)}
											>
												<item.icon size={20} className="shrink-0" />
												<span className="flex-1 overflow-hidden whitespace-nowrap transition-[width,opacity] duration-200 ease-linear group-data-[collapsible=icon]:w-0 group-data-[collapsible=icon]:flex-none group-data-[collapsible=icon]:opacity-0">
													{t(item.labelKey)}
												</span>
												<ChevronRight
													size={16}
													className={cn(
														'shrink-0 overflow-hidden transition-[width,opacity,transform] duration-200 ease-linear group-data-[collapsible=icon]:w-0 group-data-[collapsible=icon]:opacity-0',
														isOpen ? 'rotate-90' : '',
													)}
												/>
											</SidebarMenuButton>

											<div className={cn(
												'grid overflow-hidden transition-[grid-template-rows] duration-200 ease-linear group-data-[collapsible=icon]:hidden',
												isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr] pointer-events-none',
											)}>
												<div className="overflow-hidden min-h-0">
													<SidebarMenuSub className="ml-5 mr-0 border-l border-(--color-border) pl-2 pr-0 py-0">
														{item.children.map((child, childIdx) => {
															const exactMatch = location.pathname === child.path
															const insideParentSubtree = location.pathname.startsWith(item.path + '/')
															// Hidden children count too: a direct link to one shows NoAccess,
															// and no other child should light up for it.
															const allChildren = NAV_ITEMS.find((n) => n.path === item.path)?.children ?? item.children!
															const matchesAnyChild = allChildren.some((c) => c.path === location.pathname)
															const overrideMatchesGroup = sidebarOverride && item.children!.some((c) => c.path === sidebarOverride)
															const isOverrideActive = overrideMatchesGroup && sidebarOverride === child.path
															const isOwnTopLevel = NAV_ITEMS.some((n) => !n.children && n.path === location.pathname)
															const isDetailFallback = insideParentSubtree && !matchesAnyChild && !overrideMatchesGroup && !isOwnTopLevel && childIdx === 0
															const isChildActive = exactMatch || isOverrideActive || isDetailFallback
															return (
																<SidebarMenuSubItem key={child.path}>
																	<SidebarMenuSubButton
																		render={<Link to={child.path} />}
																		className={cn(
																			'h-11 px-3 text-sm rounded-(--radius-sm)',
																			isChildActive
																				? 'bg-(--sidebar-accent) text-(--sidebar-accent-foreground)'
																				: 'text-(--sidebar-foreground)',
																		)}
																		isActive={isChildActive}
																		onClick={() => {
																			if (!openMenus.includes(item.path)) {
																				setOpenMenus(prev => [...prev, item.path])
																			}
																		}}
																	>
																		{t(child.labelKey)}
																	</SidebarMenuSubButton>
																</SidebarMenuSubItem>
															)
														})}
													</SidebarMenuSub>
												</div>
											</div>
										</SidebarMenuItem>
									)
								}

								return (
									<SidebarMenuItem key={item.path}>
										<SidebarMenuButton
											render={<Link to={item.path} />}
											className="h-11 px-3 gap-2 text-sm rounded-(--radius-sm) group-data-[collapsible=icon]:gap-0"
											isActive={location.pathname === item.path}
											tooltip={t(item.labelKey)}
										>
											<item.icon size={20} className="shrink-0" />
											<span className="flex-1 overflow-hidden whitespace-nowrap transition-[width,opacity] duration-200 ease-linear group-data-[collapsible=icon]:w-0 group-data-[collapsible=icon]:flex-none group-data-[collapsible=icon]:opacity-0">
												{t(item.labelKey)}
											</span>
											{item.hasChevron && (
												<ChevronRight
													size={16}
													className="shrink-0 overflow-hidden transition-[width,opacity] duration-200 ease-linear group-data-[collapsible=icon]:w-0 group-data-[collapsible=icon]:opacity-0"
												/>
											)}
										</SidebarMenuButton>
									</SidebarMenuItem>
								)
							})}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>

			<SidebarFooter>
				<SidebarOnboarding />
			</SidebarFooter>
		</Sidebar>
	)
}
